import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { ChevronLeft, ChevronRight, GetApp, Visibility } from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useToast } from '../../contexts/ToastContext';
import { downloadUploadedFile, previewUploadedFile } from '../../utils/certificateTrainingFiles';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TAG_COLORS = {
  DUTY: { bgcolor: '#e3f2fd', borderColor: '#1976d2', color: '#0d47a1' },
  WORK: { bgcolor: '#e0f7fa', borderColor: '#00838f', color: '#006064' },
  LEAVE: { bgcolor: '#fff3e0', borderColor: '#ef6c00', color: '#e65100' },
  AVAILABILITY: { bgcolor: '#f5f5f5', borderColor: '#757575', color: '#424242' },
  ATTENDANCE: { bgcolor: '#e8f5e9', borderColor: '#2e7d32', color: '#1b5e20' },
  OVERTIME: { bgcolor: '#f3e5f5', borderColor: '#7b1fa2', color: '#4a148c' }
};

const LATE_STYLE = { bgcolor: '#ffebee', borderColor: '#c62828', color: '#b71c1c' };
const ABSENT_STYLE = { bgcolor: '#ffcdd2', borderColor: '#c62828', color: '#b71c1c' };

function tagStyle(tag) {
  if (tag.kind === 'ATTENDANCE' && tag.label === 'LATE') return LATE_STYLE;
  if (tag.kind === 'ATTENDANCE' && tag.label === 'ABSENT') return ABSENT_STYLE;
  return TAG_COLORS[tag.kind] || TAG_COLORS.AVAILABILITY;
}

const formatProfileDate = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : 'N/A');

/**
 * Employee-wise month calendar with dept-manager shift assignment.
 */
function EmployeeCalendar({
  employees,
  shifts = [],
  canAssign,
  managedDepartments,
  deptManagerAssignments = [],
  currentUserId,
  onMarked
}) {
  const { showToast } = useToast();
  const [employeeId, setEmployeeId] = useState('');
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dayOpen, setDayOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [action, setAction] = useState(canAssign ? 'assign' : 'attendance');
  const [dutyKind, setDutyKind] = useState('WORK_ORDER');
  const [dutyKinds, setDutyKinds] = useState([]);
  const [workItems, setWorkItems] = useState([]);
  const [linkedWorkId, setLinkedWorkId] = useState('');
  const [shiftId, setShiftId] = useState('');
  const [assignEndDate, setAssignEndDate] = useState('');
  const [skipWeekends, setSkipWeekends] = useState(false);
  const [attStatus, setAttStatus] = useState('PRESENT');
  const [availType, setAvailType] = useState('LEAVE');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingWork, setLoadingWork] = useState(false);

  useEffect(() => {
    if (!employees?.length) return;
    if (employeeId && employees.some((e) => String(e._id) === String(employeeId))) return;
    const self = employees.find((e) => String(e._id) === String(currentUserId));
    setEmployeeId(String(self?._id || employees[0]._id));
  }, [employees, currentUserId, employeeId]);

  const loadCalendar = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/hrms/employees/${employeeId}/calendar`, {
        params: { month }
      });
      setCalendar(res.data);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load calendar', 'error');
      setCalendar(null);
    } finally {
      setLoading(false);
    }
  }, [employeeId, month, showToast]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const dayMap = useMemo(() => {
    const map = {};
    (calendar?.days || []).forEach((d) => {
      map[d.date] = d;
    });
    return map;
  }, [calendar]);

  const cells = useMemo(() => {
    const start = dayjs(`${month}-01`);
    const daysInMonth = start.daysInMonth();
    const startPad = start.day();
    const result = [];
    for (let i = 0; i < startPad; i += 1) result.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) {
      const dateStr = start.date(d).format('YYYY-MM-DD');
      result.push(dayMap[dateStr] || { date: dateStr, tags: [], summary: {}, duties: [] });
    }
    return result;
  }, [month, dayMap]);

  const selectedEmployee = useMemo(
    () => employees.find((e) => String(e._id) === String(employeeId)),
    [employees, employeeId]
  );

  const openEmployeeFile = useCallback(async ({
    resource,
    itemId,
    fileName,
    mimeType,
    preview
  }) => {
    if (!employeeId) return;
    try {
      if (preview) {
        await previewUploadedFile({
          mode: 'user',
          entityId: employeeId,
          resource,
          itemId,
          mimeType
        });
      } else {
        await downloadUploadedFile({
          mode: 'user',
          entityId: employeeId,
          resource,
          itemId,
          fileName,
          mimeType
        });
      }
    } catch (err) {
      showToast(err.message || `Failed to ${preview ? 'preview' : 'download'} file`, 'error');
    }
  }, [employeeId, showToast]);

  const assignDepartment = selectedEmployee?.department
    || (Array.isArray(managedDepartments) ? managedDepartments[0] : null)
    || null;

  const loadDutyKinds = useCallback(async (department) => {
    if (!department) {
      setDutyKinds([]);
      return;
    }
    try {
      const res = await axios.get('/api/hrms/duty-kinds', { params: { department } });
      const kinds = res.data?.dutyKinds || [];
      setDutyKinds(kinds);
      if (kinds.length) {
        setDutyKind(kinds[0].kind);
      }
    } catch {
      setDutyKinds([{ kind: 'SHIFT', label: 'Shift only', linked: false }]);
      setDutyKind('SHIFT');
    }
  }, []);

  const loadWorkItems = useCallback(async (department, kind) => {
    if (!department || !kind || kind === 'SHIFT' || kind === 'GENERIC' || kind === 'OPS_TASK' || kind === 'SAFETY_TASK') {
      setWorkItems([]);
      setLinkedWorkId('');
      return;
    }
    setLoadingWork(true);
    try {
      const res = await axios.get('/api/hrms/assignable-work', {
        params: { department, dutyKind: kind }
      });
      setWorkItems(res.data || []);
      setLinkedWorkId('');
    } catch (err) {
      setWorkItems([]);
      showToast(err.response?.data?.message || 'Failed to load work items', 'error');
    } finally {
      setLoadingWork(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (canAssign && assignDepartment) {
      loadDutyKinds(assignDepartment);
    }
  }, [canAssign, assignDepartment, loadDutyKinds]);

  useEffect(() => {
    if (canAssign && assignDepartment && action === 'assign') {
      loadWorkItems(assignDepartment, dutyKind);
    }
  }, [canAssign, assignDepartment, dutyKind, action, loadWorkItems]);

  const openDay = (day) => {
    if (!day) return;
    setSelectedDay(day);
    setAction(canAssign ? 'assign' : 'attendance');
    setShiftId(shifts[0]?._id || '');
    setAssignEndDate(day.date);
    setSkipWeekends(false);
    setAttStatus('PRESENT');
    setAvailType('LEAVE');
    setReason('');
    setLinkedWorkId('');
    setDayOpen(true);
  };

  const saveDayAction = async () => {
    if (!selectedDay || !employeeId) return;
    setSaving(true);
    try {
      if (action === 'assign') {
        const kindMeta = dutyKinds.find((k) => k.kind === dutyKind);
        const needsLink = kindMeta?.linked;
        if (needsLink && !linkedWorkId) {
          showToast('Select a department work item', 'warning');
          setSaving(false);
          return;
        }
        if (dutyKind === 'SHIFT' && !shiftId) {
          showToast('Select a shift', 'warning');
          setSaving(false);
          return;
        }
        const work = workItems.find((w) => String(w.id) === String(linkedWorkId));
        const endDate = assignEndDate || selectedDay.date;
        if (dayjs(endDate).isBefore(dayjs(selectedDay.date), 'day')) {
          showToast('End date must be on or after the selected day', 'warning');
          setSaving(false);
          return;
        }
        const res = await axios.post('/api/hrms/roster', {
          userId: employeeId,
          startDate: selectedDay.date,
          endDate,
          skipWeekends,
          dutyKind,
          shiftId: shiftId || undefined,
          linkedEntityId: linkedWorkId || undefined,
          linkedEntityType: work?.entityType,
          linkedLabel: work?.label,
          remarks: reason
        });
        const count = res.data?.count || 1;
        showToast(
          needsLink
            ? (count > 1 ? `Department duty assigned for ${count} days` : 'Department duty assigned')
            : (count > 1 ? `Shift assigned for ${count} days` : 'Shift assigned'),
          'success'
        );
      } else if (action === 'attendance') {
        await axios.post('/api/hrms/attendance', {
          userId: employeeId,
          date: selectedDay.date,
          status: attStatus,
          remarks: reason
        });
        showToast('Attendance saved', 'success');
      } else {
        await axios.post('/api/hrms/availability', {
          userId: canAssign ? employeeId : undefined,
          type: availType,
          startDate: selectedDay.date,
          endDate: selectedDay.date,
          reason
        });
        showToast('Availability / leave submitted', 'success');
      }
      setDayOpen(false);
      await loadCalendar();
      if (onMarked) onMarked();
    } catch (err) {
      showToast(err.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const cancelDuty = async (rosterId) => {
    if (!rosterId) return;
    try {
      await axios.delete(`/api/hrms/roster/${rosterId}`);
      showToast('Duty cancelled', 'success');
      await loadCalendar();
      if (onMarked) onMarked();
      setDayOpen(false);
    } catch (err) {
      showToast(err.response?.data?.message || 'Cancel failed', 'error');
    }
  };

  const shiftMonth = (delta) => {
    setMonth(dayjs(`${month}-01`).add(delta, 'month').format('YYYY-MM'));
  };

  const managedLabel = useMemo(() => {
    if (managedDepartments == null) return 'All departments';
    if (!managedDepartments.length) return 'Own record only';
    return managedDepartments.join(', ');
  }, [managedDepartments]);

  return (
    <Box>
      {canAssign && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can assign shifts for: <strong>{managedLabel}</strong>.
          Click a day to assign duty, mark leave / late, or cancel an existing assignment.
          {deptManagerAssignments?.length > 0 && (
            <Box component="span" display="block" mt={0.5} fontSize={13}>
              Dept managers:&nbsp;
              {deptManagerAssignments.map((m) => `${m.role}→${(m.departments || []).join('/')}`).join(' · ')}
            </Box>
          )}
        </Alert>
      )}

      <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" mb={2}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Employee</InputLabel>
          <Select
            label="Employee"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
          >
            {employees.map((e) => (
              <MenuItem key={e._id} value={e._id}>
                {e.firstName} {e.lastName}
                {e.department ? ` · ${e.department}` : ''}
                {e.band?.name ? ` · ${e.band.name}` : ''}
                {(e.bands || []).length > 1
                  ? ` +${e.bands.length - 1}`
                  : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton onClick={() => shiftMonth(-1)} aria-label="Previous month"><ChevronLeft /></IconButton>
        <TextField
          type="month"
          size="small"
          label="Month"
          InputLabelProps={{ shrink: true }}
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
        <IconButton onClick={() => shiftMonth(1)} aria-label="Next month"><ChevronRight /></IconButton>
        <Button onClick={loadCalendar}>Refresh</Button>
      </Stack>

      {calendar?.counts && (
        <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
          <Chip label={`Duty ${calendar.counts.dutyDays}d`} color="primary" variant="outlined" size="small" />
          <Chip label={`Leave ${calendar.counts.leaveDays}d`} color="warning" variant="outlined" size="small" />
          <Chip label={`Late ${calendar.counts.lateDays}d`} color="error" variant="outlined" size="small" />
          <Chip label={`Absent ${calendar.counts.absentDays}d`} color="error" variant="outlined" size="small" />
          <Chip label={`OT ${calendar.counts.otEntries}`} color="secondary" variant="outlined" size="small" />
        </Stack>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
        <Chip size="small" label="Duty / shift" sx={TAG_COLORS.DUTY} />
        <Chip size="small" label="Work order / dept duty" sx={TAG_COLORS.WORK} />
        <Chip size="small" label="Leave" sx={TAG_COLORS.LEAVE} />
        <Chip size="small" label="Unavailable / Training" sx={TAG_COLORS.AVAILABILITY} />
        <Chip size="small" label="Present" sx={TAG_COLORS.ATTENDANCE} />
        <Chip size="small" label="Late / Absent" sx={LATE_STYLE} />
        <Chip size="small" label="OT" sx={TAG_COLORS.OVERTIME} />
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <Paper variant="outlined" sx={{ p: 1, mb: 3 }}>
          <Grid container columns={7} spacing={0.5}>
            {WEEKDAYS.map((w) => (
              <Grid item xs={1} key={w}>
                <Typography variant="caption" fontWeight={700} align="center" display="block" sx={{ py: 0.5 }}>
                  {w}
                </Typography>
              </Grid>
            ))}
            {cells.map((day, idx) => (
              <Grid item xs={1} key={day?.date || `pad-${idx}`}>
                {day ? (
                  <Box
                    onClick={() => openDay(day)}
                    sx={{
                      minHeight: 92,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 0.5,
                      cursor: 'pointer',
                      bgcolor: day.summary?.onLeave
                        ? '#fff8e1'
                        : day.summary?.late || day.summary?.absent
                          ? '#fff5f5'
                          : day.summary?.onDuty
                            ? '#f3f8ff'
                            : 'background.paper',
                      '&:hover': { boxShadow: 1 }
                    }}
                  >
                    <Typography variant="caption" fontWeight={600}>
                      {dayjs(day.date).date()}
                    </Typography>
                    <Stack spacing={0.25} mt={0.25}>
                      {(day.tags || []).slice(0, 3).map((tag, i) => (
                        <Box
                          key={`${tag.kind}-${tag.label}-${i}`}
                          sx={{
                            ...tagStyle(tag),
                            border: '1px solid',
                            borderRadius: 0.5,
                            px: 0.4,
                            fontSize: 10,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={`${tag.label}${tag.detail ? ` · ${tag.detail}` : ''}`}
                        >
                          {tag.label}{tag.detail ? ` ${tag.detail}` : ''}
                        </Box>
                      ))}
                      {(day.tags || []).length > 3 && (
                        <Typography variant="caption" color="text.secondary">+{day.tags.length - 3}</Typography>
                      )}
                    </Stack>
                  </Box>
                ) : (
                  <Box sx={{ minHeight: 92 }} />
                )}
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      <Typography variant="h6" gutterBottom>Duty history (manager assignments)</Typography>
      <Alert severity="info" sx={{ mb: 1 }}>
        Shifts assigned by department managers for{' '}
        {selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : 'employee'}{' '}
        in {month}.
      </Alert>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Duty type</TableCell>
              <TableCell>Work / shift</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Assigned by</TableCell>
              <TableCell>Remarks</TableCell>
              {canAssign && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {(calendar?.dutyHistory || []).map((h) => (
              <TableRow key={h.id}>
                <TableCell>{h.date}</TableCell>
                <TableCell>{h.dutyKind || 'SHIFT'}</TableCell>
                <TableCell>{h.linkedLabel || h.shiftName || ''}</TableCell>
                <TableCell>{h.startTime && h.endTime ? `${h.startTime}–${h.endTime}` : ''}</TableCell>
                <TableCell>{h.department}</TableCell>
                <TableCell>{h.assignedBy || ''}</TableCell>
                <TableCell>{h.remarks || ''}</TableCell>
                {canAssign && (
                  <TableCell>
                    <Button size="small" color="error" onClick={() => cancelDuty(h.id)}>Cancel</Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!calendar?.dutyHistory?.length && (
              <TableRow>
                <TableCell colSpan={canAssign ? 8 : 7}>No duty assignments this month</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
        Certificates
        {selectedEmployee
          ? ` · ${selectedEmployee.firstName} ${selectedEmployee.lastName}`
          : ''}
      </Typography>
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Acquired</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(calendar?.certificates || []).map((certificate) => {
              const isExpired = certificate.expiryDate
                && dayjs(certificate.expiryDate).isBefore(dayjs(), 'day');
              const isExpiringSoon = certificate.expiryDate
                && dayjs(certificate.expiryDate).diff(dayjs(), 'day') <= 60
                && !isExpired;
              return (
                <TableRow key={certificate._id} hover>
                  <TableCell>{certificate.name}</TableCell>
                  <TableCell>{(certificate.type || '').replace(/_/g, ' ')}</TableCell>
                  <TableCell>{formatProfileDate(certificate.acquiredDate)}</TableCell>
                  <TableCell>
                    {certificate.hasNoExpiry ? (
                      <Chip label="No Expiry" size="small" color="default" />
                    ) : (
                      <Chip
                        label={formatProfileDate(certificate.expiryDate)}
                        size="small"
                        color={isExpired ? 'error' : (isExpiringSoon ? 'warning' : 'default')}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {certificate.fileUrl || certificate.fileName ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          variant="outlined"
                          onClick={() => openEmployeeFile({
                            resource: 'certificates',
                            itemId: certificate._id,
                            mimeType: certificate.mimeType,
                            preview: true
                          })}
                        >
                          Preview
                        </Button>
                        <Button
                          size="small"
                          startIcon={<GetApp />}
                          variant="outlined"
                          onClick={() => openEmployeeFile({
                            resource: 'certificates',
                            itemId: certificate._id,
                            fileName: certificate.fileName || 'certificate',
                            mimeType: certificate.mimeType,
                            preview: false
                          })}
                        >
                          Download
                        </Button>
                      </Stack>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{certificate.remarks || '—'}</TableCell>
                </TableRow>
              );
            })}
            {!calendar?.certificates?.length && (
              <TableRow>
                <TableCell colSpan={6}>No certificates on file</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6" gutterBottom>
        Trainings
        {selectedEmployee
          ? ` · ${selectedEmployee.firstName} ${selectedEmployee.lastName}`
          : ''}
      </Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Acquired</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(calendar?.trainings || []).map((training) => {
              const isExpired = training.expiryDate
                && dayjs(training.expiryDate).isBefore(dayjs(), 'day');
              const isExpiringSoon = training.expiryDate
                && dayjs(training.expiryDate).diff(dayjs(), 'day') <= 60
                && !isExpired;
              return (
                <TableRow key={training._id} hover>
                  <TableCell>{training.trainingId || '—'}</TableCell>
                  <TableCell>{training.trainingName}</TableCell>
                  <TableCell>{training.trainingType || '—'}</TableCell>
                  <TableCell>{formatProfileDate(training.acquiredDate)}</TableCell>
                  <TableCell>
                    {training.hasNoExpiry ? (
                      <Chip label="No Expiry" size="small" color="default" />
                    ) : (
                      <Chip
                        label={formatProfileDate(training.expiryDate)}
                        size="small"
                        color={isExpired ? 'error' : (isExpiringSoon ? 'warning' : 'default')}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {training.fileUrl || training.fileName ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          variant="outlined"
                          onClick={() => openEmployeeFile({
                            resource: 'trainings',
                            itemId: training._id,
                            mimeType: training.mimeType,
                            preview: true
                          })}
                        >
                          Preview
                        </Button>
                        <Button
                          size="small"
                          startIcon={<GetApp />}
                          variant="outlined"
                          onClick={() => openEmployeeFile({
                            resource: 'trainings',
                            itemId: training._id,
                            fileName: training.fileName || 'training',
                            mimeType: training.mimeType,
                            preview: false
                          })}
                        >
                          Download
                        </Button>
                      </Stack>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{training.remarks || '—'}</TableCell>
                </TableRow>
              );
            })}
            {!calendar?.trainings?.length && (
              <TableRow>
                <TableCell colSpan={7}>No trainings on file</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dayOpen} onClose={() => setDayOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {selectedDay?.date}
          {calendar?.employee ? ` · ${calendar.employee.firstName} ${calendar.employee.lastName}` : ''}
        </DialogTitle>
        <DialogContent>
          {selectedDay && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>That day</Typography>
                {(selectedDay.tags || []).length === 0 && (
                  <Typography variant="body2" color="text.secondary">Nothing marked yet</Typography>
                )}
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {(selectedDay.tags || []).map((tag, i) => (
                    <Chip
                      key={`${tag.kind}-${i}`}
                      size="small"
                      label={`${tag.label}${tag.detail ? ` · ${tag.detail}` : ''}`}
                      sx={tagStyle(tag)}
                      onDelete={canAssign && (tag.kind === 'DUTY' || tag.kind === 'WORK') && tag.rosterId
                        ? () => cancelDuty(tag.rosterId)
                        : undefined}
                    />
                  ))}
                </Stack>
              </Box>
              <Divider />
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select label="Action" value={action} onChange={(e) => setAction(e.target.value)}>
                  {canAssign && (
                    <MenuItem value="assign">Assign department duty / work</MenuItem>
                  )}
                  <MenuItem value="attendance">Mark attendance (Present / Late / Absent)</MenuItem>
                  <MenuItem value="availability">Mark leave / availability</MenuItem>
                </Select>
              </FormControl>
              {action === 'assign' && (
                <>
                  <FormControl fullWidth size="small">
                    <InputLabel>Duty type</InputLabel>
                    <Select
                      label="Duty type"
                      value={dutyKind}
                      onChange={(e) => setDutyKind(e.target.value)}
                    >
                      {(dutyKinds.length ? dutyKinds : [{ kind: 'SHIFT', label: 'Shift only' }]).map((k) => (
                        <MenuItem key={k.kind} value={k.kind}>{k.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {dutyKinds.find((k) => k.kind === dutyKind)?.linked !== false
                    && dutyKind !== 'SHIFT'
                    && dutyKind !== 'GENERIC'
                    && dutyKind !== 'OPS_TASK'
                    && dutyKind !== 'SAFETY_TASK' && (
                    <FormControl fullWidth size="small">
                      <InputLabel>
                        {assignDepartment === 'MRO' ? 'Work order' : 'Work item'}
                      </InputLabel>
                      <Select
                        label={assignDepartment === 'MRO' ? 'Work order' : 'Work item'}
                        value={linkedWorkId}
                        onChange={(e) => setLinkedWorkId(e.target.value)}
                        disabled={loadingWork}
                      >
                        {workItems.map((w) => (
                          <MenuItem key={w.id} value={w.id}>{w.label}</MenuItem>
                        ))}
                      </Select>
                      {loadingWork && (
                        <Typography variant="caption" color="text.secondary">Loading work items…</Typography>
                      )}
                      {!loadingWork && !workItems.length && (
                        <Typography variant="caption" color="warning.main">
                          No open {assignDepartment || ''} work items found
                        </Typography>
                      )}
                    </FormControl>
                  )}
                  <FormControl fullWidth size="small">
                    <InputLabel>Shift (optional time band)</InputLabel>
                    <Select
                      label="Shift (optional time band)"
                      value={shiftId}
                      onChange={(e) => setShiftId(e.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {shifts.map((s) => (
                        <MenuItem key={s._id} value={s._id}>
                          {s.name} ({s.startTime}–{s.endTime})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Through date (optional range)"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={assignEndDate}
                    onChange={(e) => setAssignEndDate(e.target.value)}
                    helperText={
                      assignEndDate && assignEndDate !== selectedDay?.date
                        ? `Assigns from ${selectedDay?.date} through ${assignEndDate}`
                        : 'Leave as selected day for a single date'
                    }
                    fullWidth
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        size="small"
                        checked={skipWeekends}
                        onChange={(e) => setSkipWeekends(e.target.checked)}
                      />
                    )}
                    label="Skip weekends"
                  />
                </>
              )}
              {action === 'attendance' && (
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" value={attStatus} onChange={(e) => setAttStatus(e.target.value)}>
                    <MenuItem value="PRESENT">Present</MenuItem>
                    <MenuItem value="LATE">Late</MenuItem>
                    <MenuItem value="ABSENT">Absent</MenuItem>
                    <MenuItem value="HALF_DAY">Half day</MenuItem>
                  </Select>
                </FormControl>
              )}
              {action === 'availability' && (
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select label="Type" value={availType} onChange={(e) => setAvailType(e.target.value)}>
                    <MenuItem value="LEAVE">Leave</MenuItem>
                    <MenuItem value="UNAVAILABLE">Unavailable</MenuItem>
                    <MenuItem value="TRAINING">Training</MenuItem>
                    <MenuItem value="MEDICAL">Medical</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
              )}
              <TextField
                label="Remarks / reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
              {action === 'assign' && dutyKind === 'SHIFT' && !shifts.length && (
                <Alert severity="warning">No shifts yet — HR Manager must create shift templates first.</Alert>
              )}
              {action === 'assign' && assignDepartment === 'MRO' && (
                <Alert severity="info">
                  Assigning a work order also sets the MRO engineer on that maintenance job.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDayOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={saveDayAction}
            disabled={saving || (action === 'assign' && dutyKind === 'SHIFT' && !shifts.length)}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmployeeCalendar;
